import { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { useAuthStore } from '../lib/auth';

export function JoinRoute() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const register = useAuthStore((s) => s.register);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const navigate = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const ok = await register({ username, password });
    if (ok) navigate({ to: '/' });
  }

  return (
    <AuthShell title="Join Chess: Challenge Cards" blurb="Create a username and password. No email required.">
      <form onSubmit={submit} className="space-y-4">
        <TextField label="Username" value={username} onChange={setUsername} autoComplete="username" />
        <TextField
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
        />
        <p className="text-xs leading-relaxed text-ink-muted">
          Use at least 10 characters with a capital letter and a number or symbol. This early
          build has no password recovery, so keep it somewhere safe.
        </p>
        {error && <div className="text-sm text-danger">{error}</div>}
        <Button type="submit" size="lg" className="w-full" disabled={loading}>
          Join now
        </Button>
        <p className="text-center text-sm text-ink-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-accent hover:underline">
            Log in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function AuthShell({
  title,
  blurb,
  children,
}: {
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full w-full grid place-items-center px-5 py-6">
      <Panel className="w-full max-w-[440px] p-6">
        <h1 className="font-display text-3xl font-bold leading-tight">{title}</h1>
        <p className="mt-2 text-sm text-ink-muted">{blurb}</p>
        <div className="mt-6">{children}</div>
      </Panel>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        required
        autoComplete={autoComplete}
        className="h-11 w-full rounded-md border border-ink/10 bg-surface px-3 focus-ring"
      />
    </label>
  );
}
